import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { validateInvitationToken } from "../services/invitationService";

export function InvitationAccept() {
	const params = useParams();
	const navigate = useNavigate();
	const [status, setStatus] = useState<"loading" | "error" | "redirecting">(
		"loading",
	);
	const [invitationData, setInvitationData] = useState<{
		providerName: string;
		patientName: string;
	} | null>(null);
	const hasProcessed = useRef(false);

	useEffect(() => {
		const processInvitation = async () => {
			const token = params.token as string;

			if (!token) {
				setStatus("error");
				return;
			}

			// Prevent multiple executions
			const sessionKey = `qr_processed_${token}`;
			if (hasProcessed.current || sessionStorage.getItem(sessionKey)) {
				console.log("Invitation already being processed, skipping...");
				return;
			}

			hasProcessed.current = true;

			try {
				// Validate invitation token via era-api
				const data = await validateInvitationToken(token);

				if (!data.valid) {
					setStatus("error");
					// Clear the processing flag so user can retry
					hasProcessed.current = false;
					return;
				}

				// Mark as successfully processed only after validation succeeds
				sessionStorage.setItem(sessionKey, "true");

				// Show provider info briefly
				setInvitationData({
					providerName: data.providerName || "Provider",
					patientName: data.patientName || "New Patient",
				});

				// Store invitation token in localStorage for post-signup matching
				localStorage.setItem("invitation_token", token);
				console.log(`Stored invitation token in localStorage: ${token}`);

				// Wait a moment to show the provider info, then redirect to login with signup hint
				setTimeout(() => {
					setStatus("redirecting");
					// Redirect to login page with signup parameter
					navigate("/login?signup=true");
				}, 2000);
			} catch (error) {
				console.error("Error processing invitation:", error);
				// Clear the session storage on error so user can retry
				const sessionKey = `qr_processed_${token}`;
				sessionStorage.removeItem(sessionKey);
				hasProcessed.current = false;
				setStatus("error");
			}
		};

		processInvitation();
	}, [params.token, navigate]);

	if (status === "loading") {
		return (
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					minHeight: "100vh",
					fontFamily: "'Urbanist', system-ui, sans-serif",
					backgroundColor: "#f9fafb",
					padding: "1.5rem",
				}}
			>
				<div
					style={{
						backgroundColor: "white",
						padding: "2rem",
						borderRadius: "0.75rem",
						boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1)",
						maxWidth: "28rem",
						width: "100%",
						textAlign: "center",
					}}
				>
					<div
						style={{
							width: "3rem",
							height: "3rem",
							backgroundColor: "#dbeafe",
							borderRadius: "9999px",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							margin: "0 auto 1rem",
						}}
					>
						<div
							style={{
								width: "1.5rem",
								height: "1.5rem",
								border: "3px solid #2563eb",
								borderTopColor: "transparent",
								borderRadius: "9999px",
								animation: "spin 1s linear infinite",
							}}
						/>
					</div>
					<h2
						style={{
							fontSize: "1.125rem",
							fontWeight: 500,
							color: "#111827",
							marginBottom: "0.5rem",
						}}
					>
						Processing Invitation
					</h2>
					<p style={{ fontSize: "0.875rem", color: "#6b7280" }}>
						Please wait while we verify your invitation...
					</p>
				</div>
			</div>
		);
	}

	if (status === "error") {
		return (
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					minHeight: "100vh",
					fontFamily: "'Urbanist', system-ui, sans-serif",
					backgroundColor: "#f9fafb",
					padding: "1.5rem",
				}}
			>
				<div
					style={{
						backgroundColor: "white",
						padding: "2rem",
						borderRadius: "0.75rem",
						boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1)",
						maxWidth: "28rem",
						width: "100%",
						textAlign: "center",
					}}
				>
					<div
						style={{
							width: "3rem",
							height: "3rem",
							backgroundColor: "#fee2e2",
							borderRadius: "9999px",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							margin: "0 auto 1rem",
						}}
					>
						<svg
							style={{ width: "1.5rem", height: "1.5rem", color: "#dc2626" }}
							fill="none"
							viewBox="0 0 24 24"
							strokeWidth="2"
							stroke="currentColor"
						>
							<title>Error Icon</title>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
							/>
						</svg>
					</div>
					<h2
						style={{
							fontSize: "1.125rem",
							fontWeight: 500,
							color: "#111827",
							marginBottom: "0.5rem",
						}}
					>
						Invalid Invitation
					</h2>
					<p
						style={{
							fontSize: "0.875rem",
							color: "#6b7280",
							marginBottom: "1rem",
						}}
					>
						This invitation link is invalid or has expired. Please contact your
						healthcare provider for a new invitation.
					</p>
					<button
						type="button"
						onClick={() => navigate("/login")}
						style={{
							padding: "0.75rem 2rem",
							fontSize: "1rem",
							fontFamily: "'Urbanist', system-ui, sans-serif",
							fontWeight: 600,
							color: "#ffffff",
							backgroundColor: "#2563eb",
							border: "none",
							borderRadius: "0.5rem",
							cursor: "pointer",
							transition: "background-color 0.2s",
						}}
						onMouseEnter={(e) => {
							e.currentTarget.style.backgroundColor = "#1d4ed8";
						}}
						onMouseLeave={(e) => {
							e.currentTarget.style.backgroundColor = "#2563eb";
						}}
					>
						Go to Login
					</button>
				</div>
			</div>
		);
	}

	if (status === "redirecting" && invitationData) {
		return (
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					minHeight: "100vh",
					fontFamily: "'Urbanist', system-ui, sans-serif",
					backgroundColor: "#f9fafb",
					padding: "1.5rem",
				}}
			>
				<div
					style={{
						backgroundColor: "white",
						padding: "2rem",
						borderRadius: "0.75rem",
						boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1)",
						maxWidth: "28rem",
						width: "100%",
						textAlign: "center",
					}}
				>
					<div
						style={{
							width: "4rem",
							height: "4rem",
							backgroundColor: "#dbeafe",
							borderRadius: "9999px",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							margin: "0 auto 1rem",
						}}
					>
						<svg
							style={{ width: "2rem", height: "2rem", color: "#2563eb" }}
							fill="none"
							viewBox="0 0 24 24"
							strokeWidth="2"
							stroke="currentColor"
						>
							<title>Success Icon</title>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.623 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
							/>
						</svg>
					</div>
					<h2
						style={{
							fontSize: "1.25rem",
							fontWeight: 700,
							color: "#111827",
							marginBottom: "0.5rem",
						}}
					>
						Welcome to Era!
					</h2>
					<p
						style={{
							fontSize: "1.125rem",
							fontWeight: 500,
							color: "#374151",
							marginBottom: "0.5rem",
						}}
					>
						You're joining {invitationData.providerName}
					</p>
					{invitationData.patientName && (
						<p
							style={{
								fontSize: "0.875rem",
								color: "#6b7280",
								marginBottom: "1rem",
							}}
						>
							As: {invitationData.patientName}
						</p>
					)}
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							fontSize: "0.875rem",
							color: "#6b7280",
						}}
					>
						<div
							style={{
								width: "1rem",
								height: "1rem",
								border: "2px solid #d1d5db",
								borderTopColor: "#2563eb",
								borderRadius: "9999px",
								animation: "spin 1s linear infinite",
								marginRight: "0.5rem",
							}}
						/>
						Redirecting to signup...
					</div>
				</div>
			</div>
		);
	}

	return null;
}
